<template>
  <CartSkeleton
    v-if="
      cartStore.cartFetchState === FetchState.INITIAL ||
      (!cartStore.cart && cartStore.cartFetchState === FetchState.IN_PROGRESS)
    "
  />
  <ClientOnly v-else>
    <div class="flex flex-col gap-6 md:flex-row md:items-start" :class="[{ 'md:justify-center': !cartStore.cart }]">
      <div class="flex flex-col gap-6 md:gap-3" :class="[{ 'md:w-1/2': cartStore.cart }]">
        <div class="flex flex-row overflow-hidden rounded-xl bg-primary-50">
          <UiButton
            id="cart-tab-cart"
            variant="none"
            :icon="IconCart"
            :class="[
              'flex w-1/2 flex-col !gap-0 !rounded-none border-b-4 border-transparent !py-2',
              { '!border-primary-700': activeTab === 'cart' },
              { '!fill-neutral-500 !text-neutral-500 hover:!bg-primary-100': activeTab !== 'cart' },
            ]"
            @click="setTab('cart')"
          >
            <UiText size="lg">{{ $t('cart.cart') }}</UiText>
          </UiButton>
          <UiButton
            id="cart-tab-wishlist"
            variant="none"
            :icon="IconHeart"
            :class="[
              'flex w-1/2 flex-col !gap-0 !rounded-none border-b-4 border-transparent !py-2',
              { '!border-primary-700': activeTab === 'wishlist' },
              {
                '!fill-neutral-500 !text-neutral-500 hover:!bg-primary-100 disabled:!text-disabled-500':
                  activeTab !== 'wishlist',
              },
            ]"
            :disabled="!authStore.isLoggedIn"
            @click="setTab('wishlist')"
          >
            <UiTooltip id="cart-tab-wishlist" :label="!authStore.isLoggedIn ? $t('wishlist.loginRequired') : ''">
              <UiText size="lg">{{ $t('cart.wishlist') }}</UiText>
            </UiTooltip>
          </UiButton>
        </div>
        <div v-if="activeTab === 'cart'" class="rounded-xl bg-primary-50 p-7 md:p-12">
          <CartContent is-from-cart-page />
        </div>
        <div v-if="activeTab === 'wishlist'" class="rounded-xl bg-primary-50 p-7 md:p-12">
          <WishlistContent is-from-cart-page />
        </div>
      </div>
      <div v-if="cartStore.cart" class="w-full rounded-xl bg-white p-7 md:w-1/2 md:p-12">
        <div class="mb-3 flex flex-row items-baseline justify-between">
          <UiHeading type="h2">{{ $t('cart.summary') }}</UiHeading>
          <UiText>{{ $t('cart.itemsCount', { count: cartStore.getCartItemsTotal }) }}</UiText>
        </div>
        <CartSummary />
        <UiDivider />
        <div v-if="cartStore.cart.items.length">
          <UiButton id="cart-place-order" variant="primary" class="mt-3 w-full" @click="placeOrder()">
            {{ $t('cart.placeOrder') }}
          </UiButton>
        </div>
      </div>
    </div>
    <template #fallback>
      <CartSkeleton />
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import { IconCart, IconHeart } from '#components';

import { useAuthStore } from '~/stores/auth/authStore';
import { useCartStore } from '~/stores/cart/cartStore';

const goTo = useGoTo();
const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();

const cartStore = useCartStore();
const authStore = useAuthStore();

const activeTab = ref<'cart' | 'wishlist'>('cart');

const setTab = (tab: 'cart' | 'wishlist'): void => {
  activeTab.value = tab;

  if (tab === 'wishlist') router.replace(localePath('/cart?tab=wishlist'));
  else router.replace(localePath('/cart'));

  clickAction('tab');
};

const placeOrder = (): void => {
  goTo('/checkout');
  clickAction('place_order');
};

const clickAction = (action: string): void => {
  if (!cartStore.cart) return;

  dataLayer.push({
    cart_id: cartStore.cart.id,
    component: 'cart_page',
    event: 'button_click',
    value: action,
  });
};

watch(
  () => route.query.tab,
  () => {
    if (!authStore.isLoggedIn) setTab('cart');
    else if (typeof route.query.tab === 'string' && ['cart', 'wishlist'].includes(route.query.tab))
      setTab(route.query.tab as 'cart' | 'wishlist');
  },
  { immediate: true },
);
</script>
